<script setup lang="ts">
import type { ChipOption } from '#components/elements/ToggleChips.model';
import ToggleChips from '#components/elements/ToggleChips.vue';
import ButtonIcon from '#components/elements/button/buttonIcon/ButtonIcon.vue';
import { useResizeObserver, useScroll } from '@vueuse/core';
import { toRef, useTemplateRef } from 'vue';

defineProps<{
  options: ChipOption[];
  requiredOptions: string[];
}>();

const emits = defineEmits<{
  setSelectedDimensions: [selectedValues: string[]];
}>();

const scrollContainer = useTemplateRef<HTMLDivElement>('scrollContainer');

let { x: scrollX, arrivedState } = useScroll(scrollContainer, { behavior: 'smooth' });

const arrivedStateRef = toRef(arrivedState);

useResizeObserver(scrollContainer, () => {
  const updatedScrollData = useScroll(scrollContainer, { behavior: 'smooth' });

  scrollX = updatedScrollData.x;
  arrivedStateRef.value = updatedScrollData.arrivedState;
});

const handleUpdate = (selectedValues: string[]) => {
  emits('setSelectedDimensions', selectedValues);
};
</script>

<template>
  <div class="relative">
    <div ref="scrollContainer" class="flex mb-4 overflow-x-auto hidden-scrollbar relative z-0">
      <ToggleChips
        :min-selected-required="0"
        :options="options"
        :required-options="requiredOptions"
        direction="row"
        @update:options="handleUpdate"
      />
    </div>
    <Transition>
      <div
        v-if="!arrivedStateRef.left"
        class="absolute top-0 left-0 bottom-0 w-32 bg-gradient-to-r from-30% from-white flex items-center justify-start z-10"
      >
        <ButtonIcon icon="chevron-left" size="small" variant="default" @click="scrollX -= 150" />
      </div>
    </Transition>
    <Transition>
      <div
        v-if="!arrivedStateRef.right"
        class="absolute top-0 right-0 bottom-0 w-32 bg-gradient-to-l from-30% from-white flex items-center justify-end z-10"
      >
        <ButtonIcon icon="chevron-right" size="small" variant="default" @click="scrollX += 150" />
      </div>
    </Transition>
  </div>
</template>

<style scoped>
.v-enter-active,
.v-leave-active {
  transition: opacity 0.3s ease-in;
}

.v-enter-from,
.v-leave-to {
  opacity: 0;
}
</style>
